import React from 'react';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import {
  EmptyState,
  EmptyStateHeader,
  EmptyStateIcon,
  Flex,
  Skeleton,
  Tooltip,
  EmptyStateVariant,
  EmptyStateBody,
} from '@patternfly/react-core';
import EmptyIcon from '@patternfly/react-icons/dist/esm/icons/box-open-icon';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Duration } from 'luxon';

import { MINIMUM_COURSE_COMPLETION_PERCENTAGE } from 'config/constants';
import PositionIndicator from 'components/PositionIndicator';
import { LandingLayout } from 'components/Landing/LandingLayout';
import useMobxStores from 'hooks/useMobxStores';
import { Modality } from 'types';
import type { LearningPath } from 'types';

import './styles.scss';
import { BaseOfferingCard } from 'components/OfferingCards';
import { resolveCatalogEntry } from '../store';

interface OfferingsTabProps {
  learningPath: LearningPath | null;
  isLoading?: boolean;
  /** Minutes per lp_element (aligned with path order); from skills path aggregated metadata. */
  lpElementDurations?: (number | null)[] | null;
}

/** Average lp_elements per skills path in production */
const SKELETON_CARD_COUNT = 5;

const OfferingsTabSkeleton: React.FC = () => (
  <div data-testid="offerings-tab-skeleton">
    <LandingLayout.Progress>
      <Flex justifyContent={{ default: 'justifyContentFlexEnd' }}>
        <Skeleton width="80px" height="24px" />
      </Flex>
    </LandingLayout.Progress>
    <div className="skills-path-offerings__list" data-testid="offerings-list">
      {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
        <div key={i} className="skills-path-offerings__card-skeleton">
          <OfferingCard isLoading />
        </div>
      ))}
    </div>
  </div>
);

const OfferingsTab: React.FC<OfferingsTabProps> = ({
  learningPath,
  isLoading = false,
  lpElementDurations = null,
}) => {
  const { t } = useTranslation();
  const { catalogStore } = useMobxStores();

  const lpElements = learningPath?.lp_elements ?? [];

  const getCatalogEntry = (code: string) =>
    resolveCatalogEntry(catalogStore, code);

  const hasAnyResolvedEntry = lpElements.some(
    (el) => el?.code && getCatalogEntry(el.code),
  );
  const isCatalogPending =
    lpElements.length > 0 && !catalogStore.loaded && !hasAnyResolvedEntry;

  if (isLoading || !learningPath || isCatalogPending) {
    return <OfferingsTabSkeleton />;
  }

  const displayableElements = lpElements
    .map((element, index) => ({ element, index }))
    .filter(({ element }) => element?.code && getCatalogEntry(element.code));

  const unavailableOfferingSlugs = lpElements
    .map((element) => element?.code)
    .filter((code): code is string => !!code && !getCatalogEntry(code));

  const progressPercent = Math.round((learningPath.user_progress ?? 0) * 100);

  const progressTooltip = t(
    'Skills path progress is based on completing each offering. Courses count as completed at 75% or more. Exams are completed when passed.',
  );

  return (
    <>
      <LandingLayout.Progress>
        <Tooltip content={progressTooltip} position="top">
          <span style={{ cursor: 'default' }}>
            {t('Progress: {{progress}}%', { progress: progressPercent })}
          </span>
        </Tooltip>
      </LandingLayout.Progress>

      {displayableElements.length === 0 ? (
        <EmptyState
          data-testid="offerings-empty-state"
          variant={EmptyStateVariant.xs}
        >
          <EmptyStateHeader
            titleText={t('The offering list is empty')}
            headingLevel="h4"
            icon={<EmptyStateIcon icon={EmptyIcon} />}
          />
          <EmptyStateBody>
            {unavailableOfferingSlugs.length > 0
              ? t(
                  'This Skills path references offerings that are no longer available in the platform: {{offerings}}.',
                  { offerings: unavailableOfferingSlugs.join(', ') },
                )
              : t('This skills path has no offerings available.')}
          </EmptyStateBody>
        </EmptyState>
      ) : (
        <div
          className="skills-path-offerings__list"
          data-testid="offerings-list"
        >
          {displayableElements.map(({ element, index }) => {
            const { code } = element;
            const catalogEntry = getCatalogEntry(code)!;

            const userProgress = element.user_progress ?? 0;
            const isCompleted =
              userProgress >= MINIMUM_COURSE_COMPLETION_PERCENTAGE / 100;

            const getPositionTooltip = () => {
              if (isCompleted) {
                if (catalogEntry.modality === Modality.Exam) {
                  return t('You passed this exam');
                }
                if (catalogEntry.modality === Modality.Course) {
                  return t('You completed this course');
                }
                return t('You completed this offering');
              }
              const stepLine = t('Step {{position}} of the skills path', {
                position: index + 1,
              });
              const minutes = lpElementDurations?.[index];
              if (typeof minutes === 'number' && minutes > 0) {
                const durationLabel = Duration.fromObject({ minutes }).toFormat(
                  "h'h' mm'm'",
                );
                return (
                  <div className="skills-path-offerings__step-tooltip">
                    <div className="skills-path-offerings__step-tooltip-step">
                      {stepLine}
                    </div>
                    <div className="skills-path-offerings__step-tooltip-duration">
                      {t('Estimated duration: {{duration}}', {
                        duration: durationLabel,
                      })}
                    </div>
                  </div>
                );
              }
              return stepLine;
            };

            return (
              <div
                key={`${learningPath.code}-${code}`}
                className="skills-path-offerings__card-wrapper"
                data-testid="offerings-card-wrapper"
              >
                <PositionIndicator
                  position={index + 1}
                  isCompleted={isCompleted}
                  tooltip={getPositionTooltip()}
                />
                <BaseOfferingCard
                  entry={catalogEntry}
                  coursesShowLiveClassInfo
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default observer(OfferingsTab);
